import { useEffect, useRef, useState } from 'react';
import { formatBytes } from '../../core/format';
import { useApp } from '../../state/store';
import { useMediaPipeline } from '../hooks/useMediaPipeline';
import { useSession } from '../hooks/useSession';
import { CoverageRail } from '../parts/CoverageRail';
import { Spinner } from '../parts/bits';
import { Play } from '../parts/Icons';
import { Controls, useMediaState } from './Controls';

/**
 * Video and audio share one pipeline. The same element stays mounted when focus
 * mode toggles, so the player never re-initialises. UI-011
 */
export function MediaStage({ id, kind }: { id: string; kind: 'video' | 'audio' }) {
  const ref = useRef<HTMLMediaElement | null>(null);
  const [media, setMedia] = useState<HTMLMediaElement | null>(null);
  const focus = useApp((s) => s.focus);
  const setFocus = useApp((s) => s.setFocus);
  const { record, snap, total, complete } = useSession(id);
  const pipe = useMediaPipeline(id, ref);
  const ms = useMediaState(media);
  const feed = useApp((s) => s.feeds[id] ?? null);
  const [idle, setIdle] = useState(false);

  useEffect(() => setMedia(ref.current), []);

  // Hide controls over the picture while playing and the pointer is still. Not in non-focus layout.
  useEffect(() => {
    if (!focus) {
      setIdle(false);
      return;
    }
    let t = window.setTimeout(() => setIdle(true), 2600);
    const wake = () => {
      setIdle(false);
      window.clearTimeout(t);
      t = window.setTimeout(() => setIdle(true), 2600);
    };
    window.addEventListener('pointermove', wake);
    window.addEventListener('keydown', wake);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('pointermove', wake);
      window.removeEventListener('keydown', wake);
    };
  }, [focus]);

  const ready = pipe.phase === 'ready';
  const blocked =
    pipe.phase === 'unsupported' ||
    pipe.phase === 'error' ||
    pipe.plan === 'wait-complete' ||
    pipe.plan === 'unsupported-kind';
  const duration = pipe.info?.duration || ms?.duration || pipe.durationHint || 0;
  const seekableFraction =
    pipe.limitedSeek && total ? Math.min(1, (snap?.readable[0]?.end ?? 0) / total) : 1;

  let overlay: { title: string; body?: string; busy?: boolean } | null = null;
  if (pipe.plan === 'sniffing') overlay = { title: 'Reading the file header…', busy: true };
  else if (pipe.plan === 'wait-complete')
    overlay = {
      title: 'Preview unlocks when the download finishes',
      body: 'This format cannot be played piece by piece in this browser. Every byte is being saved once; you can export the original or play it as soon as it is complete.',
    };
  else if (pipe.plan === 'unsupported-kind')
    overlay = {
      title: 'No in-app player for this format',
      body: 'The download continues. Export the original when it is complete.',
    };
  else if (pipe.phase === 'unsupported')
    overlay = {
      title: 'This browser cannot play this file',
      body: `${pipe.error?.action ?? 'The download continues.'}${
        pipe.info?.videoCodec || pipe.info?.audioCodec
          ? ` Codecs: ${[pipe.info.videoCodec, pipe.info.audioCodec].filter(Boolean).join(', ')}.`
          : ''
      }`,
    };
  else if (pipe.phase === 'error')
    overlay = { title: pipe.error?.summary ?? 'The preview stopped', body: pipe.error?.action };
  else if (pipe.phase === 'indexing' || pipe.phase === 'idle')
    overlay = { title: pipe.waiting ?? 'Reading the file index…', busy: true };
  else if (ready && pipe.waiting) overlay = { title: pipe.waiting, busy: true };
  else if (ready && ms?.waiting) overlay = { title: 'Buffering…', busy: true };

  const playable = ready && !blocked;

  return (
    <div
      className={`stage ${focus ? 'stage--focus' : ''} ${idle ? 'stage--idle' : ''}`}
      data-kind={kind}
    >
      <div
        className="stage__screen"
        onClick={() => playable && media && (media.paused ? void media.play() : media.pause())}
      >
        {kind === 'video' ? (
          <video
            ref={ref as React.RefObject<HTMLVideoElement>}
            className="stage__media"
            playsInline
            preload="auto"
            aria-label={record?.name ?? 'Video'}
          />
        ) : (
          <>
            <audio
              ref={ref as React.RefObject<HTMLAudioElement>}
              preload="auto"
              aria-label={record?.name ?? 'Audio'}
            />
            <div className="stage__audio">
              <span className="stage__audio-name">{record?.name}</span>
              <span className="stage__audio-time mono">{formatClock(ms?.time ?? 0)}</span>
            </div>
          </>
        )}
        {kind === 'video' && playable && !overlay && ms && ms.paused && !ms.ended && (
          <button
            type="button"
            className="stage__play"
            aria-label={ms.time > 0.5 ? 'Resume playback' : 'Play'}
            onClick={(e) => {
              e.stopPropagation();
              void media?.play().catch(() => undefined);
            }}
          >
            <Play weight="fill" />
          </button>
        )}
        {overlay && (
          <div className="stage__overlay" role="status">
            {overlay.busy && <Spinner />}
            <p className="stage__overlay-title">{overlay.title}</p>
            {overlay.body && <p className="stage__overlay-body">{overlay.body}</p>}
            {!complete &&
              (pipe.plan === 'wait-complete' || pipe.plan === 'unsupported-kind') &&
              total && (
                <p className="stage__overlay-meta mono">
                  {formatBytes(snap?.counters.committedBytes ?? 0)} of {formatBytes(total)} saved
                </p>
              )}
          </div>
        )}
      </div>
      <div className="stage__bar">
        <Controls
          media={media}
          durationHint={duration || null}
          seekableFraction={seekableFraction}
          focus={focus}
          onToggleFocus={() => setFocus(!focus)}
          disabled={!playable}
        />
        {focus && snap && (
          <div className="stage__rail">
            <CoverageRail
              total={total}
              readable={snap.readable}
              inFlight={snap.inFlight}
              feed={feed}
              variant="full"
              label="Saved on this device"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function formatClock(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}
